import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import clsx from 'clsx';
import { CircleAlert } from '@/components/icons';

export const fieldBase =
  // Figma UI kit, Form Inputs (node 1:1244): 6px radius, 12px 16px padding, accent border + 3px glow on focus.
  'w-full rounded-field bg-surface-sunken px-4 py-3 text-sm/5 text-fg ring-1 ring-border ring-inset transition placeholder:text-fg-muted ' +
  'focus:shadow-focus focus:ring-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 ' +
  'aria-invalid:shadow-focus-danger aria-invalid:ring-danger-text';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid, ...rest }, ref) {
    return (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={clsx(fieldBase, className)}
        {...rest}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, ...rest },
  ref,
) {
  return <textarea ref={ref} className={clsx(fieldBase, 'min-h-24', className)} {...rest} />;
});

/** Label + control + hint/error wrapper. The child receives the generated id via render prop. */
export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (id: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={clsx('space-y-2', className)}>
      <label htmlFor={id} className="block text-xs font-medium tracking-label text-fg-secondary uppercase">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p className="flex items-center gap-2 text-xs text-danger-text" role="alert">
          <CircleAlert className="size-3 shrink-0" aria-hidden />
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-fg-muted">{hint}</p>
      )}
    </div>
  );
}
